import React, { useEffect, useRef, useState } from 'react'
import { useAtom } from 'jotai'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Badge,
  Button,
  Textarea,
  cn,
} from '@duck/ui'
import {
  Bug,
  Check,
  ImageDown,
  LayoutGrid,
  Lightbulb,
  type LucideIcon,
  MessageCircle,
  MessageSquareHeart,
  Paperclip,
  Sparkles,
  Target,
  X,
} from 'lucide-react'
import { apis, useTar } from '@/modules'
import type { TranslationKey } from '@/modules'
import { voiceDialogAtom, type VoiceTopic } from '@/store/voiceAtoms'
import { trackUserVoice, type VoiceImageMethod } from '@/modules/userVoiceAnalytics'

const TOPICS: VoiceTopic[] = ['general', 'workspace', 'challenges', 'bug', 'feature']
const MAX_BYTES = 4 * 1024 * 1024
const MAX_MESSAGE = 3000
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'] as const
const AUTO_CLOSE_MS = 4500

type ImageMimeType = (typeof ACCEPTED)[number]
type ImagePayload = { data: string; mimeType: ImageMimeType }

/** Each topic's identifying icon — gives the pills scannable identity. */
const TOPIC_ICON: Record<VoiceTopic, LucideIcon> = {
  general: MessageCircle,
  workspace: LayoutGrid,
  challenges: Target,
  bug: Bug,
  feature: Lightbulb,
}

/**
 * Maps each VoiceTopic to its literal TranslationKey so the typed-key check
 * in useTar's `t()` is satisfied (template-literal keys are not accepted).
 */
function topicLabelKey(topic: VoiceTopic): TranslationKey {
  switch (topic) {
    case 'general':
      return 'userVoice.topics.general'
    case 'workspace':
      return 'userVoice.topics.workspace'
    case 'challenges':
      return 'userVoice.topics.challenges'
    case 'bug':
      return 'userVoice.topics.bug'
    case 'feature':
      return 'userVoice.topics.feature'
  }
}

/** Topic-aware placeholder so the textarea nudges higher-quality feedback. */
function topicPlaceholderKey(topic: VoiceTopic): TranslationKey {
  switch (topic) {
    case 'general':
      return 'userVoice.placeholders.general'
    case 'workspace':
      return 'userVoice.placeholders.workspace'
    case 'challenges':
      return 'userVoice.placeholders.challenges'
    case 'bug':
      return 'userVoice.placeholders.bug'
    case 'feature':
      return 'userVoice.placeholders.feature'
  }
}

function readImage(file: File, mimeType: ImageMimeType): Promise<ImagePayload> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result)
      const comma = result.indexOf(',')
      resolve({ data: comma >= 0 ? result.slice(comma + 1) : result, mimeType })
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export const UserVoiceDialog = () => {
  const { t } = useTar()
  const [dialog, setDialog] = useAtom(voiceDialogAtom)
  const [topic, setTopic] = useState<VoiceTopic>('general')
  const [message, setMessage] = useState('')
  const [image, setImage] = useState<{ file: File; preview: string; mimeType: ImageMimeType } | null>(null)
  const [phase, setPhase] = useState<'form' | 'success'>('form')
  const [isDragging, setIsDragging] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const dragDepth = useRef(0)
  // Tracks whether the user pressed Send at least once this session, so the
  // dismiss event can distinguish "never tried" from "tried and gave up".
  const submitAttempted = useRef(false)

  useEffect(() => {
    if (dialog.open) {
      setTopic(dialog.presetTopic ?? 'general')
      setMessage('')
      setImage(null)
      setPhase('form')
      setIsDragging(false)
      dragDepth.current = 0
      submitAttempted.current = false
    }
  }, [dialog.open, dialog.presetTopic])

  useEffect(() => {
    if (phase !== 'success') return
    const id = setTimeout(() => setDialog({ open: false }), AUTO_CLOSE_MS)
    return () => clearTimeout(id)
  }, [phase, setDialog])

  // Revoke the preview object URL whenever the image changes or on unmount,
  // so resetting on reopen (setImage(null)) doesn't leak the previous URL.
  useEffect(() => {
    if (!image) return
    return () => URL.revokeObjectURL(image.preview)
  }, [image])

  const close = () => {
    // Only count as a dismiss when the user backs out of the form; the success
    // screen closes via its own auto-close timer, not this handler.
    if (phase === 'form') {
      trackUserVoice('user_voice:dismiss', {
        topic,
        hasImage: !!image,
        messageLength: message.trim().length,
        hadSubmitAttempt: submitAttempted.current,
      })
    }
    setDialog({ open: false })
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const imagePayload = image ? await readImage(image.file, image.mimeType) : undefined
      const response = await apis.userService.submitUserVoice({
        topic,
        message: message.trim(),
        image: imagePayload,
      })
      return response.data
    },
    onSuccess: () => {
      trackUserVoice('user_voice:submit:success', {
        topic,
        hasImage: !!image,
        messageLength: message.trim().length,
      })
      setPhase('success')
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      const rateLimited = status === 429
      trackUserVoice('user_voice:submit:failed', {
        topic,
        hasImage: !!image,
        messageLength: message.trim().length,
        status,
        rateLimited,
      })
      toast.error(rateLimited ? t('userVoice.rateLimited') : t('userVoice.error'))
    },
  })

  // Fires the submit-attempt event and flips the attempt flag before mutating,
  // so the funnel's attempt step is recorded even if the request later fails.
  const handleSend = () => {
    submitAttempted.current = true
    trackUserVoice('user_voice:submit', {
      topic,
      hasImage: !!image,
      messageLength: message.trim().length,
    })
    mutation.mutate()
  }

  // Validate + accept an image from any source (picker, drop, paste).
  const acceptImageFile = (file: File, method: VoiceImageMethod): void => {
    const isAccepted = (ACCEPTED as readonly string[]).includes(file.type)
    if (!isAccepted || file.size > MAX_BYTES) {
      trackUserVoice('user_voice:image_rejected', {
        reason: !isAccepted ? 'unsupported_type' : 'too_large',
        method,
        mimeType: file.type,
        sizeBytes: file.size,
      })
      toast.error(t('userVoice.imageTooLarge'))
      return
    }
    trackUserVoice('user_voice:image_attach', { method, mimeType: file.type, sizeBytes: file.size })
    setImage({ file, preview: URL.createObjectURL(file), mimeType: file.type as ImageMimeType })
  }

  const onPickImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Clear so re-selecting the same file fires onChange again.
    e.target.value = ''
    if (file) acceptImageFile(file, 'picker')
  }

  // The image effect revokes the previous preview URL on change/unmount.
  const removeImage = () => {
    trackUserVoice('user_voice:image_remove')
    setImage(null)
  }

  // Paste a screenshot straight from the clipboard into the message box.
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image/'))
    const file = item?.getAsFile()
    if (file) {
      e.preventDefault()
      acceptImageFile(file, 'paste')
    }
  }

  // Drag-and-drop, with a depth counter so child elements don't flicker the overlay.
  const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes('Files')
  const onDragEnter = (e: React.DragEvent) => {
    if (!hasFiles(e)) return
    e.preventDefault()
    dragDepth.current += 1
    setIsDragging(true)
  }
  const onDragOver = (e: React.DragEvent) => {
    if (hasFiles(e)) e.preventDefault()
  }
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setIsDragging(false)
  }
  const onDrop = (e: React.DragEvent) => {
    if (!hasFiles(e)) return
    e.preventDefault()
    dragDepth.current = 0
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) acceptImageFile(file, 'drop')
  }

  const canSend = message.trim().length > 0 && !mutation.isPending

  return (
    <Dialog open={dialog.open} onOpenChange={(open) => !open && close()}>
      <DialogContent width="sm" noMinHeight>
        {/* Brand accent bar bleeding to the dialog edges */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary/0 via-primary to-primary/0 sm:rounded-t-lg" />

        {phase === 'success' ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="relative mb-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 ring-1 ring-inset ring-primary/20 animate-in zoom-in-50 duration-300">
                <Check className="h-8 w-8 text-primary animate-in zoom-in-0 fill-mode-both duration-500 [animation-delay:150ms]" />
              </div>
              <Sparkles className="absolute -end-1 -top-1 h-5 w-5 text-primary animate-in fade-in zoom-in-50 fill-mode-both duration-500 [animation-delay:300ms]" />
            </div>
            <h2 className="text-xl font-bold animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-300 [animation-delay:100ms]">
              {t('userVoice.thankYouTitle')}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-300 [animation-delay:200ms]">
              {t('userVoice.thankYouBody')}
            </p>
          </div>
        ) : (
          <div
            className="relative"
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <DialogHeader>
              <div className="flex items-center gap-3 text-start">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
                  <MessageSquareHeart className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <DialogTitle>{t('userVoice.title')}</DialogTitle>
                  <p className="mt-0.5 text-sm text-muted-foreground">{t('userVoice.subtitle')}</p>
                </div>
              </div>
            </DialogHeader>

            <div className="mt-4 animate-in fade-in fill-mode-both duration-300 [animation-delay:75ms]">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('userVoice.topicLabel')}
              </div>
              <div className="flex flex-wrap gap-2">
                {TOPICS.map((value) => {
                  const Icon = TOPIC_ICON[value]
                  const active = topic === value
                  return (
                    <Badge
                      key={value}
                      role="button"
                      aria-pressed={active}
                      variant="outline"
                      className={cn(
                        'cursor-pointer gap-1.5 px-3 py-1.5 text-sm transition-all',
                        active
                          ? 'border-primary/40 bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:border-primary/30 hover:text-foreground',
                      )}
                      onClick={() => {
                        if (value !== topic) {
                          trackUserVoice('user_voice:topic_change', { from: topic, to: value })
                          setTopic(value)
                        }
                      }}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {t(topicLabelKey(value))}
                    </Badge>
                  )
                })}
              </div>
            </div>

            <div className="mt-4 animate-in fade-in fill-mode-both duration-300 [animation-delay:150ms]">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('userVoice.messageLabel')}
              </div>
              <div className="relative">
                <Textarea
                  value={message}
                  maxLength={MAX_MESSAGE}
                  placeholder={t(topicPlaceholderKey(topic))}
                  onChange={(e) => setMessage(e.target.value)}
                  onPaste={onPaste}
                  className="min-h-[140px] resize-y bg-background pb-7 transition-colors focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                />
                <div className="pointer-events-none absolute bottom-2 end-3 text-xs tabular-nums text-muted-foreground">
                  {message.length} / {MAX_MESSAGE}
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-3 animate-in fade-in fill-mode-both duration-300 [animation-delay:225ms]">
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPTED.join(',')}
                className="hidden"
                onChange={onPickImage}
                data-testid="user-voice-file-input"
              />
              {image ? (
                <div className="relative">
                  <img src={image.preview} alt="" className="h-14 w-14 rounded-md object-cover ring-1 ring-inset ring-border" />
                  <button
                    type="button"
                    aria-label={t('userVoice.removeImage')}
                    onClick={removeImage}
                    className="absolute -end-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => fileRef.current?.click()}
                >
                  <Paperclip className="me-2 h-4 w-4" />
                  {t('userVoice.attachImage')}
                </Button>
              )}
              <span className="text-xs text-muted-foreground">
                {image ? t('userVoice.imageAttached') : t('userVoice.imageHint')}
              </span>
            </div>

            <div className="mt-6 flex justify-end gap-2 animate-in fade-in fill-mode-both duration-300 [animation-delay:300ms]">
              <Button variant="outline" onClick={close}>
                {t('userVoice.cancel')}
              </Button>
              <Button disabled={!canSend} onClick={handleSend}>
                {mutation.isPending ? t('userVoice.sending') : t('userVoice.send')}
              </Button>
            </div>

            {/* Drag-and-drop overlay */}
            {isDragging && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary bg-card/90 backdrop-blur-sm animate-in fade-in duration-150">
                <ImageDown className="h-8 w-8 text-primary" />
                <span className="text-sm font-medium text-primary">{t('userVoice.dropHere')}</span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
